import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../services/api';
import './ProductCarousel.css';
import { proxyBucketUrl, handleImageError } from '../utils/imageUtils';

interface ProductCarouselProps {
  products: Product[];
  title?: string;
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({ products, title = "You Might Also Like" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const itemsPerView = {
    mobile: 1,
    tablet: 2,
    desktop: 3
  };

  const updateScrollButtons = () => {
    if (!carouselRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [products]);

  const scrollTo = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return;

    const itemWidth = carouselRef.current.children[0]?.clientWidth || 0;
    const gap = 32; // 2rem gap
    const scrollDistance = itemWidth + gap;

    if (direction === 'left') {
      carouselRef.current.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
      setCurrentIndex(Math.max(0, currentIndex - 1));
    } else {
      carouselRef.current.scrollBy({ left: scrollDistance, behavior: 'smooth' });
      setCurrentIndex(Math.min(products.length - 1, currentIndex + 1));
    }

    setTimeout(updateScrollButtons, 300);
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="product-carousel">
      <h2 className="carousel-title">{title}</h2>
      
      <div className="carousel-container">
        <button 
          className={`carousel-btn carousel-btn-left ${!canScrollLeft ? 'disabled' : ''}`}
          onClick={() => scrollTo('left')}
          disabled={!canScrollLeft}
        >
          ←
        </button>

        <div 
          className="carousel-track"
          ref={carouselRef}
          onScroll={updateScrollButtons}
        >
          {products.map((product) => (
            <Link 
              key={product.id} 
              to={`/product/${product.id}`} 
              className="carousel-item"
            >
              <div className="carousel-item-image">
                <img 
                  src={proxyBucketUrl(product.images[0])} 
                  alt={product.name}
                  onError={handleImageError}
                />
                {product.originalPrice && (
                  <div className="carousel-discount-badge">
                    -{Math.round(((Number(product.originalPrice) - Number(product.price)) / Number(product.originalPrice)) * 100)}%
                  </div>
                )}
              </div>
              
              <div className="carousel-item-info">
                <h4 className="carousel-item-title">{product.name}</h4>
                <div className="carousel-item-price">
                  <span className="current-price">€{Number(product.price).toFixed(2)}</span>
                  {product.originalPrice && (
                    <span className="original-price">€{Number(product.originalPrice).toFixed(2)}</span>
                  )}
                </div>
                {product.category && (
                  <span className="carousel-item-category">{product.category.name}</span>
                )}
              </div>
            </Link>
          ))}
        </div>

        <button 
          className={`carousel-btn carousel-btn-right ${!canScrollRight ? 'disabled' : ''}`}
          onClick={() => scrollTo('right')}
          disabled={!canScrollRight}
        >
          →
        </button>
      </div>

      <div className="carousel-dots">
        {Array.from({ length: Math.ceil(products.length / 3) }).map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${Math.floor(currentIndex / 3) === index ? 'active' : ''}`}
            onClick={() => {
              const scrollPosition = index * 3 * (carouselRef.current?.children[0]?.clientWidth || 0);
              carouselRef.current?.scrollTo({ left: scrollPosition, behavior: 'smooth' });
              setCurrentIndex(index * 3);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductCarousel;